import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import {
    clearAIVoiceFiltersStorage,
    fetchAIVoiceResults,
    saveAIVoiceFiltersToStorage,
} from 'js/src/data/ai-voices-and-avatars/ai-voice-modal/ai-voice-modal-operations';
import {
    getAIVoiceModalFilters,
    getAIVoiceModalRequestIsPending,
    getAIVoiceModalRequestIsUninitialized,
} from 'js/src/data/ai-voices-and-avatars/ai-voice-modal/ai-voice-modal-selectors';
import type {
    AIVoiceFilters,
    AIVoiceSortValue,
} from 'js/src/data/ai-voices-and-avatars/ai-voice-modal/ai-voice-modal-slice';
import {
    clearAIVoiceSearch,
    createAIVoiceSearch,
} from 'js/src/data/ai-voices-and-avatars/ai-voice-modal/ai-voice-modal-slice';
import type { ChangeFilterCallback } from 'js/src/data/ai-voices-and-avatars/ai-voice-modal/ai-voice-modal-types';
import type { AppDispatch, GetRootState } from 'js/src/libs/RootState';
import { useAppDispatch } from 'js/src/libs/RootState';

/**
 * Do not export or use this method anywhere outside this hook.
 * @internal
 */
const handleTriggerFetch = () => {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        /**
         * Get updated filters
         */
        const {
            page,
            search,
            accent,
            tone,
            style,
            age,
            gender,
            quality,
            languages,
            favourites,
            sort,
        } = getAIVoiceModalFilters(getState());

        /**
         * Build updated query
         */
        const query = {
            page: page ?? undefined,
            accent: accent ?? undefined,
            tone: tone ?? undefined,
            style: style ?? undefined,
            age: age ?? undefined,
            gender: gender ?? undefined,
            quality: quality ?? undefined,
            language: languages.length > 0 ? languages : undefined,
            search: search || undefined,
            favourites: favourites || undefined,
            sort: sort ?? undefined,
        };

        /**
         * Perform actual fetch
         */
        dispatch(fetchAIVoiceResults(query));
    };
};

const useAIVoiceSearch = () => {
    const dispatch = useAppDispatch();

    /**
     * Any public consumer should use this method to fetch AI voices.
     * Do not use `handleTriggerFetch` to fetch AI voices anywhere in code.
     */
    const triggerFetch = useCallback(() => {
        dispatch(handleTriggerFetch());
    }, [dispatch]);

    const isUninitialized = useSelector(getAIVoiceModalRequestIsUninitialized);
    const isFetching = useSelector(getAIVoiceModalRequestIsPending);

    const {
        page,
        search: searchTerm,
        accent,
        tone,
        style,
        age,
        quality,
        gender,
        languages,
        favourites,
        sort,
    } = useSelector(getAIVoiceModalFilters);

    const hasNextPage = page !== null;

    const onClearFilters = useCallback(() => {
        dispatch(clearAIVoiceSearch());
        clearAIVoiceFiltersStorage();
        triggerFetch();
    }, [dispatch, triggerFetch]);

    const onChangeFilter = useCallback(
        (updatedFilters: Partial<AIVoiceFilters>) => {
            dispatch(createAIVoiceSearch(updatedFilters));
            dispatch(saveAIVoiceFiltersToStorage());
            triggerFetch();
        },
        [dispatch, triggerFetch],
    );

    const onChangeQuality = useCallback<ChangeFilterCallback>(
        (value) => {
            onChangeFilter({ quality: value });
        },
        [onChangeFilter],
    );

    const onChangeGender = useCallback<ChangeFilterCallback>(
        (value) => {
            onChangeFilter({ gender: value });
        },
        [onChangeFilter],
    );

    const onChangeAge = useCallback<ChangeFilterCallback>(
        (value) => {
            onChangeFilter({ age: value });
        },
        [onChangeFilter],
    );

    const onChangeAccent = useCallback<ChangeFilterCallback>(
        (value) => {
            onChangeFilter({ accent: value });
        },
        [onChangeFilter],
    );

    const onChangeTone = useCallback<ChangeFilterCallback>(
        (value) => {
            onChangeFilter({ tone: value });
        },
        [onChangeFilter],
    );

    const onChangeStyle = useCallback<ChangeFilterCallback>(
        (value) => {
            onChangeFilter({ style: value });
        },
        [onChangeFilter],
    );

    const onChangeLanguage = useCallback<ChangeFilterCallback>(
        (value) => {
            onChangeFilter({ languages: value ? [value] : [] });
        },
        [onChangeFilter],
    );

    const onChangeSearchTerm = useCallback<ChangeFilterCallback>(
        (value) => {
            if (value !== searchTerm) {
                onChangeFilter({ search: value });
            }
        },
        [onChangeFilter, searchTerm],
    );

    const onChangeFavourites = useCallback(() => {
        onChangeFilter({ favourites: !favourites });
    }, [onChangeFilter, favourites]);

    const onChangeSort = useCallback(
        (value: AIVoiceSortValue) => {
            onChangeFilter({ sort: value });
        },
        [onChangeFilter],
    );

    const selectedAccent = accent;
    const selectedTone = tone;
    const selectedStyle = style;
    const selectedQuality = quality;
    const selectedGender = gender;
    const selectedAge = age;
    const selectedLanguage = languages[0] ?? null;

    const selectedFavourites = favourites;
    const selectedSort = sort;

    const hasActiveFilters = !!(
        selectedTone ||
        selectedStyle ||
        selectedAccent ||
        selectedLanguage ||
        selectedQuality ||
        selectedGender ||
        selectedAge ||
        selectedFavourites
    );

    return {
        triggerFetch,
        isUninitialized,
        isFetching,
        hasNextPage,
        searchTerm,
        selectedAccent,
        selectedTone,
        selectedStyle,
        selectedQuality,
        selectedGender,
        selectedAge,
        selectedLanguage,
        onChangeAge,
        onChangeGender,
        onChangeQuality,
        onChangeAccent,
        onChangeTone,
        onChangeStyle,
        onChangeLanguage,
        onChangeSearchTerm,
        selectedFavourites,
        onChangeFavourites,
        selectedSort,
        onChangeSort,
        hasActiveFilters,
        onClearFilters,
    };
};

export default useAIVoiceSearch;
